import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { databaseApi } from '../api/database'
import { FileCheck, Database, ArrowRight, User, Bot } from 'lucide-react'

export default function HomePage() {
  const { data: humanCount, isLoading: humanLoading } = useQuery({
    queryKey: ['checklist-count', 'human'],
    queryFn: async () => {
      const res = await databaseApi.list({ page: 1, page_size: 1, published_only: false, source: 'human' })
      return res.total
    },
  })

  const { data: aiCount, isLoading: aiLoading } = useQuery({
    queryKey: ['checklist-count', 'ai'],
    queryFn: async () => {
      const res = await databaseApi.list({ page: 1, page_size: 1, published_only: false, source: 'ai' })
      return res.total
    },
  })

  const isLoading = humanLoading || aiLoading

  return (
    <div className="max-w-7xl mx-auto px-4 py-16">
      <div className="text-center mb-16">
        <img
          src="/Graphical_abstract.jpg"
          alt="EV-Checklist graphical abstract"
          className="mx-auto mb-6 w-auto max-h-[20rem] rounded-xl shadow-sm"
        />
        <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
          A modern, questionnaire-driven checklist for reporting extracellular vesicle studies.
          Generate a secret code, fill the form, and download your HTML or PDF report.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/checklist"
            className="inline-flex items-center gap-2 bg-brand-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-brand-700 transition-colors"
          >
            <FileCheck className="w-5 h-5" />
            Start a Checklist
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/database"
            className="inline-flex items-center gap-2 bg-white text-gray-700 border border-gray-300 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
          >
            <Database className="w-5 h-5" />
            Browse Database
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl border shadow-sm p-8 text-center hover:shadow-md transition-shadow">
          <div className="w-14 h-14 bg-brand-50 rounded-xl flex items-center justify-center mx-auto mb-5">
            <FileCheck className="w-7 h-7 text-brand-600" />
          </div>
          <h3 className="text-xl font-semibold mb-2">Submit Checklist</h3>
          <p className="text-gray-600">Create or resume a checklist with your secret code. Save drafts and submit when ready.</p>
        </div>
        <div className="bg-white rounded-2xl border shadow-sm p-8 text-center hover:shadow-md transition-shadow">
          <div className="flex items-center justify-center gap-6 mb-3">
            <div className="text-center">
              <div className="text-4xl font-bold text-brand-600">{isLoading ? '…' : (humanCount ?? 0).toLocaleString()}</div>
              <div className="flex items-center justify-center gap-1 text-sm text-gray-600 mt-1">
                <User className="w-4 h-4" />
                Human
              </div>
            </div>
            <div className="h-10 w-px bg-gray-200" />
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600">{isLoading ? '…' : (aiCount ?? 0).toLocaleString()}</div>
              <div className="flex items-center justify-center gap-1 text-sm text-gray-600 mt-1">
                <Bot className="w-4 h-4" />
                AI
              </div>
            </div>
          </div>
          <h3 className="text-xl font-semibold mb-2">Submitted Checklists</h3>
          <p className="text-gray-600">Total checklists in the public database by source.</p>
        </div>
        <div className="bg-white rounded-2xl border shadow-sm p-8 text-center hover:shadow-md transition-shadow">
          <div className="w-14 h-14 bg-brand-50 rounded-xl flex items-center justify-center mx-auto mb-5">
            <Database className="w-7 h-7 text-brand-600" />
          </div>
          <h3 className="text-xl font-semibold mb-2">Browse Database</h3>
          <p className="text-gray-600">Search, filter, and export published checklists as CSV.</p>
        </div>
      </div>
    </div>
  )
}
